/**
 * useMapLabelPositions — Single shared RAF loop for all label overlays
 *
 * Replaces 16 individual map event listeners (4 events x 4 overlays) with
 * a single `map.on('move')` listener. Positions are stored in MutableRefObjects
 * (not state) so camera movement triggers zero React re-renders.
 *
 * Consumers register via `onFrame(callback)` to imperatively update DOM transforms.
 */

import { useEffect, useRef, useCallback } from 'react'
import { labelProjection, type ScreenPosition } from '@/services/LabelProjectionService'
import { useAircraftEditorStore } from '@/stores/aircraftEditorStore'
import { calculateUnitCenterPosition } from '@/lib/utils/unit-position'
import type { PlacedAircraft3D } from '@/services/3d'
import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'

// ── Types ──

export interface LabelPositionData {
  /** Aircraft label positions (allocation ID → screen pos) */
  aircraft: Map<string, ScreenPosition>
  /** Area/building label positions (building ID → screen pos) */
  areas: Map<string, ScreenPosition>
  /** Unit label positions (composite key → screen pos) */
  units: Map<string, ScreenPosition>
  /** Aircraft body positions for leader lines (allocation ID → screen pos at model center) */
  aircraftBody: Map<string, ScreenPosition>
}

type FrameCallback = (positions: LabelPositionData) => void

interface UseMapLabelPositionsOptions {
  map: mapboxgl.Map | null
  mapCenter: [number, number] | null | undefined
  aircraftData: PlacedAircraft3D[]
  buildings: Building3DConfigExtended[]
  showAreaLabels: boolean
  showUnitLabels: boolean
  showLeaderLines: boolean
  selectedAllocationId: string | null
}

export function useMapLabelPositions({
  map,
  mapCenter,
  aircraftData,
  buildings,
  showAreaLabels,
  showUnitLabels,
  showLeaderLines,
  selectedAllocationId,
}: UseMapLabelPositionsOptions) {
  // Subscriber callbacks — called every frame with new positions
  const subscribersRef = useRef<Set<FrameCallback>>(new Set())
  // RAF guard to coalesce rapid events
  const rafIdRef = useRef<number | null>(null)
  // Latest positions ref (for initial sync when components mount)
  const latestPositionsRef = useRef<LabelPositionData>({
    aircraft: new Map(),
    areas: new Map(),
    units: new Map(),
    aircraftBody: new Map(),
  })

  // Store current data in refs to avoid recreating the move handler
  const dataRef = useRef({
    aircraftData,
    buildings,
    showAreaLabels,
    showUnitLabels,
    showLeaderLines,
    selectedAllocationId,
  })
  dataRef.current = {
    aircraftData,
    buildings,
    showAreaLabels,
    showUnitLabels,
    showLeaderLines,
    selectedAllocationId,
  }

  const mapCenterRef = useRef(mapCenter)
  mapCenterRef.current = mapCenter

  /**
   * Core projection routine — runs inside RAF
   */
  const computeAndNotify = useCallback(() => {
    rafIdRef.current = null
    const m = map
    const center = mapCenterRef.current
    if (!m || !center) return

    const {
      aircraftData: aircraft,
      buildings: blds,
      showAreaLabels: showAreas,
      showUnitLabels: showUnits,
      showLeaderLines: showLeaders,
      selectedAllocationId: selectedId,
    } = dataRef.current

    // Begin frame — caches refMercator, scale, canvasSize
    labelProjection.beginFrame(m, { lat: center[1], lng: center[0] })

    const positions: LabelPositionData = {
      aircraft: new Map(),
      areas: new Map(),
      units: new Map(),
      aircraftBody: new Map(),
    }

    // ── Aircraft positions ──
    // Project ALL aircraft — cheap with cached context (~50 map.project calls).
    // Overlays filter by their own visibility criteria.
    const editorState = useAircraftEditorStore.getState()

    for (const ac of aircraft) {
      const edited = editorState.editedAllocations.get(ac.id)
      const pos = edited?.currentPosition ?? ac.position

      if (ac.id === selectedId) {
        // Selected aircraft gets a different yOffset
        positions.aircraft.set(ac.id, labelProjection.project(pos, -60))
      } else {
        positions.aircraft.set(ac.id, labelProjection.project(pos, -40))

        // Leader line endpoints — project to aircraft model center
        if (showLeaders) {
          const heightFt = ac.physicalDimensions?.height ?? 10
          const zoom = m.getZoom()
          const pitch = m.getPitch()
          const heightMeters = heightFt * 0.3048
          const zoomScale = Math.pow(2, zoom - 18) * 4
          const pitchFactor = Math.max(0.3, Math.sin((pitch * Math.PI) / 180))
          const modelCenterOffset = -(heightMeters * 0.5 * zoomScale * pitchFactor)
          positions.aircraftBody.set(ac.id, labelProjection.project(pos, modelCenterOffset))
        }
      }
    }

    // ── Area labels ──
    if (showAreas) {
      for (const bld of blds) {
        const heightOffset = -Math.min(80, bld.height * 2)
        const pos = labelProjection.project(
          bld.position as [number, number, number],
          heightOffset
        )
        if (pos.visible) {
          positions.areas.set(bld.id, pos)
        }
      }
    }

    // ── Unit labels ──
    if (showUnits) {
      for (const bld of blds) {
        const units = bld.generatedUnits
        if (!units || units.length === 0) continue

        for (const unit of units) {
          const unitCenter = calculateUnitCenterPosition(
            unit,
            bld,
            bld.templateType === 'double_row'
          )
          const pos = labelProjection.project(
            [unitCenter.x, unitCenter.y, unitCenter.z],
            0
          )
          if (pos.visible) {
            positions.units.set(`${bld.id}:${unit.code}`, pos)
          }
        }
      }
    }

    // Store latest and notify subscribers
    latestPositionsRef.current = positions
    for (const cb of subscribersRef.current) {
      cb(positions)
    }
  }, [map])

  /**
   * Schedule a position update on the next animation frame
   */
  const scheduleUpdate = useCallback(() => {
    if (rafIdRef.current != null) return
    rafIdRef.current = requestAnimationFrame(computeAndNotify)
  }, [computeAndNotify])

  // ── Single map event listener ──
  useEffect(() => {
    if (!map) return

    map.on('move', scheduleUpdate)

    // Initial computation
    scheduleUpdate()

    return () => {
      map.off('move', scheduleUpdate)
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
    }
  }, [map, scheduleUpdate])

  // ── Re-compute when data changes (not camera) ──
  useEffect(() => {
    scheduleUpdate()
  }, [
    aircraftData,
    buildings,
    showAreaLabels,
    showUnitLabels,
    showLeaderLines,
    selectedAllocationId,
    scheduleUpdate,
  ])

  /**
   * Subscribe to frame updates. Returns an unsubscribe function.
   * Immediately calls the callback with the latest positions.
   */
  const onFrame = useCallback((callback: FrameCallback): (() => void) => {
    subscribersRef.current.add(callback)
    // Immediate sync with latest positions
    callback(latestPositionsRef.current)
    return () => {
      subscribersRef.current.delete(callback)
    }
  }, [])

  /**
   * Force a re-computation (e.g. after data mutation)
   */
  const forceUpdate = useCallback(() => {
    scheduleUpdate()
  }, [scheduleUpdate])

  return { onFrame, forceUpdate, latestPositionsRef }
}
